import { StatusMenuOption } from './status-menu.util';

/**
 * Status options for list tabs / pill menus. Colours match the legacy bg-* pill
 * classes in styles.scss so tabs, pills and menu dots agree.
 */
export interface ListStatusOption extends StatusMenuOption {
  color: string;
  className: string;
}

export const INVOICE_STATUS_OPTIONS: ListStatusOption[] = [
  { value: 'DRAFT', label: 'Draft', color: '#b7b7b7', className: 'bg-in-draft' },
  { value: 'SENT', label: 'Sent', color: '#efb43d', className: 'bg-in-sent' },
  { value: 'UNPAID', label: 'Unpaid', color: '#6da4ff', className: 'bg-in-unpaid' },
  { value: 'PAID', label: 'Paid', color: '#90bd79', className: 'bg-in-paid' },
  { value: 'OVERDUE', label: 'Overdue', color: '#ff9e5a', className: 'bg-in-overdue' },
  {
    value: 'PARTIAL_PAYMENT_MADE',
    label: 'Partial payment',
    color: '#51adcf',
    className: 'bg-in-partial-payment',
  },
  { value: 'CANCELLED', label: 'Cancelled', color: '#e76e70', className: 'bg-in-cancelled' },
  { value: 'ON_HOLD', label: 'On hold', color: '#f17ab4', className: 'bg-in-on-hold' },
];

export const LEAD_STATUS_OPTIONS: ListStatusOption[] = [
  { value: 'NEW', label: 'New Leads', color: '#9c68d0', className: 'bg-ld-new' },
  { value: 'PROPOSAL_SENT', label: 'Leads Sent', color: '#efb43d', className: 'bg-ld-proposal-sent' },
  { value: 'NEGOTIATION', label: 'Leads In Negotiation', color: '#51adcf', className: 'bg-ld-negotiation' },
  { value: 'WON', label: 'Leads Won', color: '#90bd79', className: 'bg-ld-won' },
  { value: 'LOST', label: 'Leads Lost', color: '#e76e70', className: 'bg-ld-lost' },
  { value: 'HOLD', label: 'Leads on Hold', color: '#f17ab4', className: 'bg-ld-hold' },
];

/** Labels follow SETTINGS.QUOTATION_STATUSES (API values unchanged). */
export const QUOTATION_STATUS_OPTIONS: ListStatusOption[] = [
  { value: 'NEW_QUOTE', label: 'New Lead', color: '#9c68d0', className: 'bg-qo-new-quote' },
  { value: 'ESTIMATING', label: 'Lead Lost', color: '#6b7280', className: 'bg-qo-estimating' },
  { value: 'READY_TO_PRICING', label: 'New Quotes', color: '#51adcf', className: 'bg-qo-ready-pricing' },
  { value: 'READY_TO_SEND', label: 'Site Inspection', color: '#ff9e5a', className: 'bg-qo-ready-send' },
  { value: 'SENT', label: 'Quotes Sent', color: '#efb43d', className: 'bg-qo-sent' },
  { value: 'REVISION', label: 'Quotes In Negotiation', color: '#f17ab4', className: 'bg-qo-revision' },
  { value: 'APPROVED', label: 'Quotes Won', color: '#90bd79', className: 'bg-qo-approved' },
  { value: 'REJECTED', label: 'Quotes Lost', color: '#e76e70', className: 'bg-qo-rejected' },
  { value: 'HOLD', label: 'Quotes on Hold', color: '#34d399', className: 'bg-qo-hold' },
];

export const JOB_STATUS_PILL_OPTIONS: ListStatusOption[] = [
  { value: 'PLANNING', label: 'Planning', color: '#9c68d0', className: 'bg-jb-planning' },
  { value: 'IN_PROGRESS', label: 'In progress', color: '#efb43d', className: 'bg-jb-in-progress' },
  { value: 'HOLD', label: 'On hold', color: '#51adcf', className: 'bg-jb-hold' },
  { value: 'COMPLETED', label: 'Completed', color: '#90bd79', className: 'bg-jb-completed' },
  { value: 'CANCELLED', label: 'Cancelled', color: '#e76e70', className: 'bg-jb-cancelled' },
];

export const TASK_STATUS_OPTIONS: ListStatusOption[] = [
  { value: 'PLANNING', label: 'Planning', color: '#9c68d0', className: 'bg-tk-planning' },
  { value: 'IN_PROGRESS', label: 'In progress', color: '#efb43d', className: 'bg-tk-in-progress' },
  { value: 'HOLD', label: 'Hold', color: '#51adcf', className: 'bg-tk-hold' },
  { value: 'COMPLETED', label: 'Completed', color: '#90bd79', className: 'bg-tk-completed' },
  { value: 'CANCELLED', label: 'Cancelled', color: '#e76e70', className: 'bg-tk-cancelled' },
  { value: 'REJECTED', label: 'Rejected', color: '#b7b7b7', className: 'bg-in-rejected' },
];

/**
 * CR-35 — five inventory location/lifecycle statuses.
 * Drop off + On site are one UI status; canonical write value is ON_SITE.
 * Legacy DROP_OFF rows still resolve via findListStatus / inventoryStatusFilterValues.
 */
export const INVENTORY_STATUS_OPTIONS: ListStatusOption[] = [
  { value: 'IN_WAREHOUSE', label: 'In Warehouse', color: '#90bd79', className: 'bg-iy-warehouse' },
  { value: 'PICK_UP', label: 'Pick up from Warehouse', color: '#efb43d', className: 'bg-iy-pickup' },
  {
    value: 'ON_SITE',
    label: 'Drop off / On site',
    color: '#e76e70',
    className: 'bg-iy-drop-off',
  },
  { value: 'RETURN_REQUEST', label: 'Return request', color: '#9c68d0', className: 'bg-iy-return-request' },
  { value: 'RETURN_TO_WAREHOUSE', label: 'Return to Warehouse', color: '#34d399', className: 'bg-iy-returned' },
];

/** Statuses that require hire details (client/job/dates) before save. */
export const INVENTORY_HIRE_STATUSES = new Set([
  'PICK_UP',
  'DROP_OFF',
  'ON_SITE',
  'RETURN_REQUEST',
]);

/** Normalize legacy DROP_OFF → ON_SITE for writes. */
export function normalizeInventoryStatus(
  value: string | null | undefined
): string | null | undefined {
  if (value === 'DROP_OFF') return 'ON_SITE';
  return value;
}

/** Values to send when filtering by a palette status (legacy DROP_OFF under ON_SITE). */
export function inventoryStatusFilterValues(
  status: string | null | undefined
): string[] | null {
  if (!status) return null;
  if (status === 'ON_SITE' || status === 'DROP_OFF') {
    return ['ON_SITE', 'DROP_OFF'];
  }
  return [status];
}

export const PRIORITY_STATUS_OPTIONS: ListStatusOption[] = [
  { value: 'HIGH', label: 'High', color: '#e76e70', className: 'bg-high' },
  { value: 'MEDIUM', label: 'Medium', color: '#34d399', className: 'bg-medium' },
  { value: 'LOW', label: 'Low', color: '#f2d066', className: 'bg-low' },
];

export function findListStatus(
  options: ListStatusOption[],
  value: string | null | undefined
): ListStatusOption | undefined {
  if (value == null || value === '') return undefined;
  const direct = options.find(o => o.value === value);
  if (direct) return direct;
  // Legacy Drop off rows map to merged Drop off / On site
  if (value === 'DROP_OFF' && options === INVENTORY_STATUS_OPTIONS) {
    return options.find(o => o.value === 'ON_SITE');
  }
  if (value === 'DROP_OFF') {
    return options.find(o => o.value === 'ON_SITE');
  }
  return undefined;
}
